import type { Bulletin } from '@shared/types/Bulletin';
import type { Conge } from '@shared/types/Conge';
import type { Salarie } from '@shared/types/Salarie';
import type { AttestationType } from '@shared/types/Attestation';

function todayIso(now = new Date()): string {
  return now.toISOString().slice(0, 10);
}

function latestBulletin(bulletins: readonly Bulletin[], salarieId: string): Bulletin | undefined {
  return bulletins
    .filter((item) => item.salarie_id === salarieId)
    .sort((a, b) => b.periode.localeCompare(a.periode))[0];
}

function enPoste(salarie: Salarie): boolean {
  return salarie.statut === 'actif' || salarie.statut === 'suspendu' || salarie.statut === 'conge';
}

/**
 * Solde de tout compte : la date de sortie réelle est obligatoire (elle fixe la fin
 * du contrat ; le reçu n'est pas opposable au travailleur, art. 183 du Code du travail).
 */
function estSorti(salarie: Salarie): boolean {
  return Boolean(salarie.date_sortie);
}

/**
 * Congé éligible pour attestation : statut métier `valide` ou `pris`
 * (libellé UI « Approuvé »). On n’exige plus que la période soit future —
 * un congé déjà terminé reste attestatif.
 */
export function congePourAttestation(
  conges: readonly Conge[],
  salarieId: string,
  today = todayIso(),
): Conge | undefined {
  const list = conges.filter(
    (item) =>
      item.salarie_id === salarieId && (item.statut === 'valide' || item.statut === 'pris'),
  );
  if (list.length === 0) return undefined;
  const enCours = list.filter((item) => item.date_debut <= today && item.date_fin >= today);
  if (enCours.length > 0) {
    return enCours.slice().sort((a, b) => a.date_debut.localeCompare(b.date_debut))[0];
  }
  // Sinon le plus récent (date_fin desc)
  return list.slice().sort((a, b) => b.date_fin.localeCompare(a.date_fin))[0];
}

export function messageAttestationVide(type: AttestationType): string {
  switch (type) {
    case 'conges':
      return 'Aucun salarié n’a de congé approuvé ou en cours. Validez d’abord une demande.';
    case 'fin_contrat':
      return 'Aucun salarié sorti avec une date de sortie renseignée. Complétez l’onglet « Sortie » du salarié.';
    case 'salaire':
    case 'cnss':
      return 'Aucun salarié n’a de bulletin. Calculez d’abord la paie.';
    case 'travail':
    case 'presence':
      return 'Aucun salarié en poste pour ce document.';
    default:
      return 'Aucun salarié éligible pour cette attestation.';
  }
}

export function salariesEligiblesAttestation(
  type: AttestationType,
  salaries: readonly Salarie[],
  bulletins: readonly Bulletin[],
  conges: readonly Conge[],
  today = todayIso(),
): Salarie[] {
  return salaries.filter((salarie) => {
    if (type === 'travail' || type === 'presence') return enPoste(salarie);
    if (type === 'fin_contrat') return estSorti(salarie);
    if (type === 'salaire' || type === 'cnss')
      return Boolean(latestBulletin(bulletins, salarie.id));
    if (type === 'conges') return Boolean(congePourAttestation(conges, salarie.id, today));
    return false;
  });
}
